import React from "react";
import { useSelector } from "react-redux"; // 1. Import useSelector
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as d3 from "d3";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
    boxShadow: "none",
    overflowX: "hidden",
    border: `1px solid ${rccColors.materialGrey[100]}`,
    borderRadius: "4px 4px 0 0",
  },
  table: {
    width: "100%",
    // height: "100%",
    tableLayout: "fixed",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    backgroundColor: rccColors.materialGrey[100],
  },
  headerCell: {
    color: rccColors.neutral[600],
    borderBottom: `1px solid ${rccColors.materialGrey[200]}`,
    backgroundColor: rccColors.materialGrey[100],
    padding: "8px 12px",
    whiteSpace: "normal",
    textAlign: "center",
    verticalAlign: "middle",
  },
  dataCell: {
    borderBottom: `1px solid ${rccColors.materialGrey[200]}`,
    color: rccColors.neutral[700],
    padding: "12px 12px",
    verticalAlign: "middle",
    whiteSpace: "normal",
    wordWrap: "break-word",
  },
  lastRow: {
    "& td": {
      borderBottom: "none",
    },
  },
  rankCell: {
    color: rccColors.neutral[700],
    textAlign: "center",
  },
  emptyMessage: {
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
    boxShadow: "none",
    overflowX: "hidden",
    color: rccColors.neutral[400],
    border: `1px solid ${rccColors.materialGrey[100]}`,
    borderRadius: "4px 4px 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "16px",
  },
}));

const FocusTable = ({ data, config, emptyMessage }) => {
  const classes = useStyles();
  const typography = useRccTypography();

  // Get Currency Symbol from Redux
  const currencySymbol = useSelector((state) => state.app.user?.Currency);
  const Value_Label = useSelector((state) => state.app.user?.Value_Label);

  const allowedColumns = [
    "Rank",
    "Opportunity Name",
    "Current Stage",
    "ACV",
    "Proj. Close Date",
  ];

  const filteredConfig = config
    ? allowedColumns
        .map((allowedCol) =>
          config.find((col) => {
            // Clean strings for comparison
            const uiCol = col.UI_column
              ? col.UI_column.toLowerCase().replace(/[_\s.]/g, "")
              : "";
            const tableCol = col.table_column
              ? col.table_column.toLowerCase().replace(/[_\s.]/g, "")
              : "";
            const allowed = allowedCol.toLowerCase().replace(/[_\s.]/g, "");

            // 1. Check strict UI match (e.g. "Rank", "Current Stage")
            if (uiCol === allowed) return true;

            // 2. Check table_column match (Fixes ACV vs SOV)
            // If allowed is "acv" and table_column is "acv", this passes
            if (tableCol === allowed) return true;

            // 3. Handle special case aliases
            if (allowed === "opportunityname") {
              return uiCol === "oppname" || tableCol === "oppname";
            }

            return false;
          }),
        )
        .filter(Boolean)
    : [];

  // Updated to take the whole column object to check data_type/table_column
  const getColumnWidth = (col) => {
    const name = col.UI_column ? col.UI_column.toLowerCase() : "";
    const tableCol = col.table_column ? col.table_column.toLowerCase() : "";
    const type = col.data_type ? col.data_type.toLowerCase() : "";

    if (name === "rank") return "8%";

    // Check type OR table_column OR name for ACV
    if (type === "acv" || tableCol === "acv" || name === "acv") return "10%";

    if (name.includes("date") || type === "date") return "16%";
    if (name.includes("stage")) return "20%";
    return "40%";
  };

  if (!data || data.length === 0) {
    return (
      <div className={classes.emptyMessage}>
        <Typography className={typography.textMediumSemibold}>
          {emptyMessage || "No data available"}
        </Typography>
      </div>
    );
  }

  const sortedData = [...data]
    .filter((row) => row.Rank != null && row.Rank >= 1)
    .slice(0, 5);

  if (sortedData.length === 0) {
    return (
      <div className={classes.emptyMessage}>
        <Typography className={typography.textMediumSemibold}>
          {emptyMessage || "No data available"}
        </Typography>
      </div>
    );
  }

  const rowCount = sortedData.length;

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table
        className={classes.table}
        aria-label="focus table"
        style={{ height: rowCount > 4 ? "100%" : "auto" }}
      >
        <TableHead>
          <TableRow>
            {filteredConfig.map((col) => {
              let headerLabel = col.UI_column;
              if (headerLabel === "Rank") headerLabel = "Company Rank";
              if (headerLabel === "Opportunity Name") headerLabel = "Opp Name";
              if (headerLabel.toLowerCase() === "acv" && Value_Label)
                headerLabel = Value_Label;

              return (
                <TableCell
                  key={col.UI_column}
                  align="center"
                  className={`${classes.headerCell} ${typography.textExtraSmallSemibold}`}
                  style={{ width: getColumnWidth(col) }}
                >
                  {headerLabel}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row, idx) => {
            const isLastRow = idx === sortedData.length - 1;

            return (
              <TableRow
                key={idx}
                hover
                className={isLastRow ? classes.lastRow : ""}
              >
                {filteredConfig.map((col, cIdx) => {
                  const value = row[col.UI_column];
                  let formattedValue = value;

                  // Apply Local Formatting using d3 logic + Redux Currency
                  if (col.data_type === "acv" && value != null) {
                    // Divide by 1000 and round
                    const kValue = Math.round(value / 1000);
                    // Use d3.format for comma separation if needed (e.g. 1,000K), though usually K values are small enough
                    const formattedNumber = d3.format(",")(kValue);
                    formattedValue = `${currencySymbol}${formattedNumber}K`;
                  } else if (col.data_type === "date" && value) {
                    const dateObj = new Date(value);
                    formattedValue = !isNaN(dateObj.getTime())
                      ? `${
                          dateObj.getMonth() + 1
                        }/${dateObj.getDate()}/${dateObj.getFullYear()}`
                      : value;
                  }

                  // Determine Alignment
                  // 1. Use config alignment if available
                  let align = col.align || "left";

                  // 2. Fallback logic if config is missing 'align'
                  if (!col.align) {
                    if (col.data_type === "acv" || col.data_type === "date")
                      align = "right";
                    if (col.UI_column === "Rank") align = "center";
                  }

                  const isRank = col.UI_column === "Rank";

                  return (
                    <TableCell
                      key={cIdx}
                      align={align}
                      className={`
                        ${classes.dataCell} 
                        ${isRank ? classes.rankCell : ""} 
                        ${typography.textSmallRegular}
                      `}
                    >
                      {formattedValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FocusTable;
